import { JstDate } from '../../core/time/business-time';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SubscriptionService, subscriptionError } from '../../core/services/subscription.service';
import { MemberService } from '../../core/services/member.service';
import { StudioService } from '../../core/services/studio.service';
import { TransactionService } from '../../core/services/transaction.service';
import { NotificationService } from '../../core/services/notification.service';
import { SubscriptionRequestComponent } from './subscription-request.component';
import { deliveryDayLabel } from './subscription-date';
import { resolveCreateTransactionDay } from '../notification/notification-deadline';
import { PageTitleService } from '../../core/services/page-title.service';
import { SubscriptionStateLegendComponent, subscriptionState } from './subscription-state-legend.component';
import { SubscriptionDialogFocusDirective } from './subscription-dialog-focus.directive';

@Component({
    selector: 'app-subscription-member',
    imports: [CommonModule, FormsModule, RouterLink, SubscriptionRequestComponent, SubscriptionStateLegendComponent, SubscriptionDialogFocusDirective],
    templateUrl: './subscription-member.component.html'
})
export class SubscriptionMemberComponent implements OnInit {
  private service = inject(SubscriptionService);
  private memberService = inject(MemberService);
  private studioService = inject(StudioService);
  private transactionService = inject(TransactionService);
  private notificationService = inject(NotificationService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private pageTitle = inject(PageTitleService);
  readonly memberId = Number(this.route.snapshot.paramMap.get('memberId'));
  readonly requestOnly = this.route.snapshot.data['requestOnly'] === true;
  readonly deliveryDayLabel = deliveryDayLabel;
  readonly state = subscriptionState;
  readonly days = Array.from({ length: 31 }, (_, i) => i + 1);
  readonly statusNames = ['申請中', '確認中', '確認済', '削除済', '期限切れ'];
  member: any;
  products: any[] = [];
  items: any[] = [];
  requests: any[] = [];
  includeEnded = false;
  transactionDay = 11;
  loading = false;
  refreshFailed = false;
  saving = false;
  requestSaving = false;
  error = '';
  success = '';
  requestKind: 'start' | 'stop' | null = null;
  editing: any = null;
  deliveryDay: number | null = null;
  selectedRequest: any = null;
  targetStatus: number | null = null;
  readonly requestStateIcon = (status: number): string => ['fa-bullhorn', 'fa-edit', 's-icon-finished', 's-icon-close', 'fa-calendar-times-o'][status] ?? '';
  readonly requestStateClass = (status: number): string => ['text-danger', 'text-warning', 'text-success', 'text-secondary', 'text-secondary'][status] ?? '';
  get availableRequestStatuses(): number[] { return Number(this.selectedRequest?.status) === 1 ? [0, 2, 3] : [2, 3]; }
  openRequestStatus(item: any): void {
    if (Number(item.status) !== 0 || this.saving || this.requestSaving || this.loading || this.refreshFailed) return;
    this.selectedRequest = item;
    this.targetStatus = null;
  }

  get hasActiveSubscriptions(): boolean { return this.items.some(item => item.status === '契約中'); }

  closeDialog(): void {
    if (this.saving || this.requestSaving) return;
    this.requestKind = null;
    this.editing = null;
    this.selectedRequest = null;
  }
  cancelRequest(): void {
    if (this.requestSaving) return;
    if (this.requestOnly) void this.router.navigate(['/notification', this.memberId]);
    else this.requestKind = null;
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if ((!this.requestOnly && this.requestKind) || this.editing || this.selectedRequest) this.closeDialog();
  }

  async ngOnInit(): Promise<void> {
    this.pageTitle.set(this.requestOnly ? '諸届申請 / 物販定期便' : '物販定期便', 'hand_package', true, 'material-symbols-outlined');
    this.loading = true;
    try {
      const [member, schedule] = await Promise.all([this.memberService.getOne(this.memberId), this.transactionService.getBatchSchedule()]);
      this.member = member;
      this.transactionDay = resolveCreateTransactionDay(schedule);
      this.products = ((await this.studioService.getProduct(member.studio.id)) ?? []).filter((item: any) => Number(item.productType) === 5 && Number(item.status) === 0 && !item.deletedAt);
      await this.reload();
    } catch (error) { this.error = subscriptionError(error); }
    finally { this.loading = false; }
  }

  async reload(): Promise<void> {
    this.loading = true;
    try {
      const [items, requests] = await Promise.all([this.service.getByMember(this.memberId, this.includeEnded), this.service.getRequests(this.memberId)]);
      this.items = Array.isArray(items) ? items : [];
      this.requests = (requests ?? []).filter((item: any) => [10, 11].includes(Number(item.requestType)));
      this.refreshFailed = false;
      this.error = '';
    } catch {
      this.refreshFailed = true;
      throw new Error('一覧を取得できませんでした。表示中の一覧は更新前の情報です。「一覧を再取得」で最新の状態を確認してください。');
    } finally { this.loading = false; }
  }

  async retryReload(): Promise<void> {
    if (this.loading || this.saving || this.requestSaving) return;
    try { await this.reload(); } catch (error) { this.error = subscriptionError(error); }
  }

  async loadSubscriptions(): Promise<void> {
    try { await this.reload(); }
    catch (error) { this.error = subscriptionError(error); }
  }

  async requestSaved(): Promise<void> {
    this.requestSaving = false;
    this.success = `物販定期便の利用${this.requestKind === 'start' ? '開始' : '停止'}申請を作成しました`;
    this.requestKind = null;
    this.error = '';
    try { await this.reload(); } catch (error) { this.error = subscriptionError(error); }
  }

  editDeliveryDay(item: any): void {
    if (this.refreshFailed || item.status === '終了') return;
    this.editing = item;
    this.deliveryDay = item.deliveryDay == null ? null : Number(item.deliveryDay);
    this.error = '';
  }

  async saveDeliveryDay(): Promise<void> {
    if (this.refreshFailed || this.saving || !this.editing || this.editing.status === '終了' || (this.deliveryDay !== null && !this.days.includes(Number(this.deliveryDay)))) return;
    await this.mutate(async () => {
      await this.service.updateDeliveryDay(this.memberId, { subscriptionId: this.editing.id, deliveryDay: this.deliveryDay === null ? null : Number(this.deliveryDay) });
      this.editing = null;
      this.success = '物販定期便の引渡日を更新しました';
    });
  }

  async changeStatus(): Promise<void> {
    if (this.refreshFailed || this.saving || !this.selectedRequest || ![0, 1].includes(Number(this.selectedRequest.status)) || (this.targetStatus === null || !this.availableRequestStatuses.includes(Number(this.targetStatus)))) return;
    await this.mutate(async () => {
      await this.notificationService.changeStatus({ id: this.selectedRequest.id, status: Number(this.targetStatus), ...(Number(this.targetStatus) === 2 ? { acceptedAt: new JstDate() } : {}) });
      this.selectedRequest = null;
      this.success = '申請ステータスを更新しました';
    });
  }

  private async mutate(action: () => Promise<void>): Promise<void> {
    this.saving = true;
    this.error = '';
    this.success = '';
    try { await action(); await this.reload(); }
    catch (error) { this.error = subscriptionError(error); }
    finally { this.saving = false; }
  }
}
